// useFeedbackPrompt — Proaktives Feedback-Prompt-System ("Scout fragt nach")
//
// Phase-2-Impl F-4 (Spec: Dokumentation/Projekte/Aktive Projekte/PROJ-Feedback-Prompt-System.md,
// TQ #7073 / Bundle #7268). Geteilter Hook fuer customer-portal + org-portal.
//
// Backend-Contract (portal-api, Spec Abschnitt 2 — implementiert in F-2 #7269):
//   GET  /api/me/pending-prompts          -> { success, prompt: {id, question} | null }
//   POST /api/me/prompts/{id}/respond     -> Body { response_text }
//   POST /api/me/prompts/{id}/skip        -> Body { forever }
//
// Token wird vom aufrufenden Panel durchgereicht (Spec-Signatur `useFeedbackPrompt(token, panelOpen)`):
// beide Portale liefern ihn aus `useAuth()` von `@it-sce/ui/contexts/KeycloakAuthContext`.
// (Bewusst NICHT `useAuthenticatedFetch` aus hooks/useAuth — das nutzt einen anderen,
// in den Portalen nicht gemounteten AuthContext und wuerde "Not authenticated" werfen.)

import { useState, useEffect, useCallback, useRef } from 'react'

/** Ein vom Staff kuratierter, dem User aktuell anstehender Feedback-Prompt. */
export interface FeedbackPrompt {
  id: number
  question: string
}

export interface UseFeedbackPromptResult {
  /** Der eine anstehende Prompt, oder null. Treibt den Pulse-Indicator. */
  pendingPrompt: FeedbackPrompt | null
  /** Antwort senden (Freitext). Leert danach pendingPrompt. */
  respond: (responseText: string) => Promise<void>
  /** Ueberspringen. `forever=true` => "nicht mehr zeigen" (irreversibel). Leert pendingPrompt. */
  skip: (forever: boolean) => Promise<void>
}

/** Ruhiges Poll-Intervall — kein WebSocket (Spec Abschnitt 3). */
const POLL_INTERVAL_MS = 5 * 60 * 1000
/** Relativer API-Pfad — nginx-Proxy haengt das Backend an (beide Portale). */
const API_BASE = '/api'

/**
 * Pollt anstehende Feedback-Prompts und stellt respond/skip bereit.
 *
 * @param token Keycloak Access-Token (aus `useAuth().token`). null => kein Poll.
 * @param panelOpen ob das Scout-Panel offen ist. Offen => Pulse sofort weg
 *   (Spec UX-Regel "Oeffnen entfernt Pulse sofort, auch ohne Antwort") und kein Poll.
 */
export function useFeedbackPrompt(
  token: string | null,
  panelOpen: boolean,
): UseFeedbackPromptResult {
  const [pendingPrompt, setPendingPrompt] = useState<FeedbackPrompt | null>(null)

  // token + panelOpen in Refs halten, damit das Poll-Intervall stabil bleibt
  // (kein Re-Subscribe bei jedem Token-Refresh / Panel-Toggle).
  const tokenRef = useRef(token)
  tokenRef.current = token
  const panelOpenRef = useRef(panelOpen)
  panelOpenRef.current = panelOpen

  const authedFetch = useCallback((path: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    }
    if (tokenRef.current) headers['Authorization'] = `Bearer ${tokenRef.current}`
    return fetch(`${API_BASE}${path}`, { ...options, headers })
  }, [])

  const poll = useCallback(async () => {
    // Ohne Token oder bei offenem Panel nicht pollen (kein Pulse waehrend offen).
    if (!tokenRef.current || panelOpenRef.current) return
    try {
      const res = await authedFetch('/me/pending-prompts')
      if (!res.ok) return
      const data = (await res.json()) as { success?: boolean; prompt?: FeedbackPrompt | null }
      setPendingPrompt(data?.prompt ?? null)
    } catch {
      // Feedback ist unkritisch — Netz-/Auth-Fehler still ignorieren, naechster Tick versucht es erneut.
    }
  }, [authedFetch])

  // Poll beim Mount + ruhiges Intervall.
  useEffect(() => {
    void poll()
    const id = setInterval(() => void poll(), POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [poll])

  // Panel geoeffnet => Pulse sofort entfernen.
  useEffect(() => {
    if (panelOpen) setPendingPrompt(null)
  }, [panelOpen])

  const respond = useCallback(async (responseText: string) => {
    const prompt = pendingPrompt
    if (!prompt) return
    try {
      await authedFetch(`/me/prompts/${prompt.id}/respond`, {
        method: 'POST',
        body: JSON.stringify({ response_text: responseText }),
      })
    } finally {
      setPendingPrompt(null)
    }
  }, [authedFetch, pendingPrompt])

  const skip = useCallback(async (forever: boolean) => {
    const prompt = pendingPrompt
    if (!prompt) return
    try {
      await authedFetch(`/me/prompts/${prompt.id}/skip`, {
        method: 'POST',
        body: JSON.stringify({ forever }),
      })
    } finally {
      setPendingPrompt(null)
    }
  }, [authedFetch, pendingPrompt])

  return { pendingPrompt, respond, skip }
}
